import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "../../api.js";
import { useAuth } from "../../context/AuthContext.jsx";
import { ForumProvider, useForum } from "./ForumContext.jsx";
import "./Forum.css";

const TYPE_LABELS = {
  all:        { ar: "الكل",     en: "All" },
  discussion: { ar: "نقاش",    en: "Discussion" },
  artwork:    { ar: "أعمال",   en: "Artwork" },
  book:       { ar: "كتاب",    en: "Book" },
  pdf:        { ar: "PDF",      en: "PDF" },
  video:      { ar: "فيديو",   en: "Video" },
};

function timeSince(date) {
  const diff = (Date.now() - new Date(date).getTime()) / 1000;
  if (diff < 60)   return "الآن";
  if (diff < 3600) return `${Math.floor(diff / 60)} د`;
  if (diff < 86400)return `${Math.floor(diff / 3600)} س`;
  return `${Math.floor(diff / 86400)} يوم`;
}

function TypeBadge({ type }) {
  const map = { discussion: "tbadge-discussion", artwork: "tbadge-artwork", book: "tbadge-book", pdf: "tbadge-pdf", video: "tbadge-video" };
  const label = { discussion: "نقاش", artwork: "أعمال", book: "كتاب", pdf: "PDF", video: "فيديو" };
  return <span className={`tbadge ${map[type] || "tbadge-discussion"}`}>{label[type] || type}</span>;
}

function CategoryPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn, token } = useAuth();
  const { categories, isMember } = useForum();

  const [cat, setCat]           = useState(null);
  const [threads, setThreads]   = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [pages, setPages]       = useState(1);
  const [typeFilter, setTypeFilter] = useState("all");
  const [loading, setLoading]   = useState(true);

  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle]     = useState("");
  const [newTitleAr, setNewTitleAr] = useState("");
  const [newContent, setNewContent] = useState("");
  const [newType, setNewType]         = useState("discussion");
  const [newThumbnail, setNewThumbnail] = useState("");
  const [thumbUploading, setThumbUploading] = useState(false);
  const [submitting, setSubmitting]   = useState(false);

  useEffect(() => {
    const found = categories.find(c => c.slug === slug);
    if (found) setCat(found);
  }, [categories, slug]);

  const loadThreads = useCallback(async () => {
    if (!cat) return;
    setLoading(true);
    try {
      const params = { categoryId: cat._id, page, limit: 20 };
      if (typeFilter !== "all") params.type = typeFilter;
      const { data } = await axios.get(`${API_BASE}/api/forum/threads`, { params });
      setThreads(data.threads);
      setTotal(data.total);
      setPages(data.pages);
    } catch {
      setThreads([]);
    } finally {
      setLoading(false);
    }
  }, [cat, page, typeFilter]);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  const uploadThumbnail = async (file) => {
    setThumbUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await fetch(`${API_BASE}/api/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Upload failed");
      setNewThumbnail(data.url);
    } catch (err) {
      alert(err.message);
    } finally {
      setThumbUploading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API_BASE}/api/forum/threads`,
        { categoryId: cat._id, title_en: newTitle.trim(), title_ar: newTitleAr.trim(), contentEn: newContent.trim(), type: newType, thumbnail: newThumbnail || undefined },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewTitle(""); setNewTitleAr(""); setNewContent(""); setNewThumbnail(""); setShowCreate(false);
      setPage(1); loadThreads();
    } catch (err) {
      alert(err.response?.data?.error || "حدث خطأ");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="forum-wrap">
      {/* Breadcrumb */}
      <div className="forum-breadcrumb">
        <Link to="/Forum">المنتدى</Link>
        <span className="forum-breadcrumb-sep">›</span>
        <span>{cat?.name_ar || slug}</span>
      </div>

      {/* Category header */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24, flexWrap: "wrap" }}>
        <span style={{ fontSize: "2rem" }}>{cat?.icon || "💬"}</span>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: "1.4rem", fontWeight: 800, margin: 0 }}>{cat?.name_ar || slug}</h1>
          <div style={{ fontSize: "0.82rem", color: "var(--text-3)" }}>{cat?.name_en}</div>
          {cat?.description_ar && <div style={{ fontSize: "0.85rem", color: "var(--text-2)", marginTop: 4 }}>{cat.description_ar}</div>}
        </div>
        {isMember && cat?.type !== "archive" && (
          <button className="forum-reply-submit" onClick={() => setShowCreate(s => !s)}>
            {showCreate ? "إلغاء" : "+ نقاش جديد"}
          </button>
        )}
        {!isMember && isLoggedIn && (
          <Link to="/Forum/register" className="forum-reply-submit" style={{ textDecoration: "none" }}>انضم للمنتدى</Link>
        )}
        {!isLoggedIn && (
          <Link to="/login" className="forum-reply-submit" style={{ textDecoration: "none" }}>دخول</Link>
        )}
      </div>

      {/* Create thread form */}
      {showCreate && (
        <form className="forum-create-form" onSubmit={handleCreate}>
          <div className="forum-form-group">
            <label className="forum-form-label">نوع المشاركة</label>
            <select className="forum-form-select" value={newType} onChange={e => setNewType(e.target.value)}>
              <option value="discussion">💬 نقاش</option>
              <option value="artwork">🖼 أعمال فنية</option>
              <option value="book">📚 كتاب</option>
              <option value="pdf">📄 PDF</option>
              <option value="video">🎬 فيديو</option>
            </select>
          </div>
          <div className="forum-form-group">
            <label className="forum-form-label">العنوان (عربي)</label>
            <input className="forum-form-input" value={newTitleAr} onChange={e => setNewTitleAr(e.target.value)} placeholder="عنوان النقاش..." />
          </div>
          <div className="forum-form-group">
            <label className="forum-form-label">Title (English)</label>
            <input className="forum-form-input" style={{ direction: "ltr" }} value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="Thread title..." required />
          </div>
          <div className="forum-form-group">
            <label className="forum-form-label">المحتوى</label>
            <textarea className="forum-form-textarea" rows={4} value={newContent} onChange={e => setNewContent(e.target.value)} placeholder="اكتب هنا..." />
          </div>
          <div className="forum-form-group">
            <label className="forum-form-label">صورة الغلاف (اختياري)</label>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {newThumbnail && <img src={newThumbnail} alt="" style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 6 }} />}
              <label style={{ display: "inline-block", padding: "7px 14px", border: "1px solid var(--border)", borderRadius: 6, cursor: thumbUploading ? "default" : "pointer", fontSize: "0.85rem", background: "var(--bg-2)" }}>
                {thumbUploading ? "جارٍ الرفع…" : "📁 رفع صورة"}
                <input type="file" accept="image/*" style={{ display: "none" }} disabled={thumbUploading}
                  onChange={e => { const f = e.target.files[0]; if (f) uploadThumbnail(f); }} />
              </label>
              {newThumbnail && <button type="button" onClick={() => setNewThumbnail("")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-3)", fontSize: "0.85rem" }}>✕ حذف</button>}
            </div>
          </div>
          <button type="submit" className="forum-submit-btn" disabled={submitting || thumbUploading}>
            {submitting ? "جارٍ النشر..." : "نشر النقاش"}
          </button>
        </form>
      )}

      {/* Type filter chips */}
      <div className="forum-chips">
        {Object.entries(TYPE_LABELS).map(([k, v]) => (
          <button key={k} className={`forum-chip ${typeFilter === k ? "active" : ""}`} onClick={() => { setTypeFilter(k); setPage(1); }}>
            {v.ar}
          </button>
        ))}
      </div>

      {/* Thread list */}
      {loading ? (
        <div className="forum-loading">جارٍ التحميل…</div>
      ) : threads.length === 0 ? (
        <div className="forum-empty">
          لا توجد مشاركات في هذا القسم بعد
          {isMember && <div style={{ marginTop: 12 }}><button className="forum-reply-submit" onClick={() => setShowCreate(true)}>كن أول من يبدأ نقاشاً</button></div>}
        </div>
      ) : (
        threads.map(thread => (
          <Link key={thread._id} to={`/Forum/thread/${thread._id}`} className="forum-thread-row">
            {thread.isPinned && <span className="forum-pin-badge" title="مثبّت">📌</span>}
            {thread.thumbnail ? (
              <img className="forum-thread-thumb" src={thread.thumbnail} alt="" />
            ) : (
              <img
                className="forum-thread-avatar"
                src={thread.authorAvatar || ""}
                alt={thread.authorName}
                onError={e => { e.target.style.display = "none"; }}
              />
            )}
            <div className="forum-thread-body">
              <div className="forum-thread-title">
                <TypeBadge type={thread.type} />
                {" "}
                {thread.title_ar || thread.title_en}
              </div>
              <div className="forum-thread-meta">
                <span>{thread.authorName}</span>
                <span>{timeSince(thread.createdAt)}</span>
                {thread.lastReplyBy && <span>آخر رد: {thread.lastReplyBy}</span>}
                {thread.isLocked && <span className="forum-lock-badge">🔒 مغلق</span>}
              </div>
            </div>
            <div className="forum-thread-stats">
              <div>👁 {thread.viewCount}</div>
              <div>💬 {thread.replyCount}</div>
              <div>❤️ {thread.likeCount}</div>
            </div>
          </Link>
        ))
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="forum-pagination">
          {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
            <button key={p} className={`forum-page-btn ${p === page ? "active" : ""}`} onClick={() => setPage(p)}>
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ForumCategory() {
  return (
    <ForumProvider>
      <CategoryPage />
    </ForumProvider>
  );
}
